// @ts-nocheck
'use client'

import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Slider } from '@/components/ui/slider'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Play, Loader2, TrendingUp, TrendingDown, Activity,
  BarChart3, AlertCircle, CheckCircle2, Target, DollarSign,
  Rocket, Grid3x3, Waves, Brain, Zap
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { BacktestingService, type BacktestResult } from '@/services/backtesting-service'
import type { AutoTradingConfig } from '@/services/auto-trading-service'
import { useChartData } from '../../chart/chart-data-context'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'

interface BacktestingPanelProps {
  symbol: string
}

// Chart Type Strategies
const CHART_STRATEGIES = [
  {
    id: 'ai-agents',
    chartType: 'ai-agents',
    name: 'AI Agents',
    description: 'Support/resistance AI analysis',
    icon: Brain,
    color: 'text-purple-500',
    defaultConfig: { minConfidence: 82, stopLoss: 2, takeProfit: 6 }
  },
  {
    id: 'otc',
    chartType: 'otc',
    name: 'OTC Pivots',
    description: 'Breakeven crossover signals',
    icon: Target,
    color: 'text-orange-500',
    defaultConfig: { minConfidence: 80, stopLoss: 1.5, takeProfit: 4 }
  },
  {
    id: 'powerhouse',
    chartType: 'powerhouse',
    name: 'PowerHouse',
    description: 'Market structure CHoCH',
    icon: Zap,
    color: 'text-yellow-500',
    defaultConfig: { minConfidence: 85, stopLoss: 2, takeProfit: 5 }
  },
  {
    id: 'footprint',
    chartType: 'footprint',
    name: 'Footprint',
    description: 'Volume delta analysis',
    icon: Activity,
    color: 'text-cyan-500',
    defaultConfig: { minConfidence: 65, stopLoss: 2, takeProfit: 4 }
  },
  {
    id: 'money-flow',
    chartType: 'money-flow-profile',
    name: 'Money Flow',
    description: 'VWAP volume breakouts',
    icon: DollarSign,
    color: 'text-green-500',
    defaultConfig: { minConfidence: 70, stopLoss: 2, takeProfit: 4 }
  },
  {
    id: 'zoi-fractals',
    chartType: 'zoi-fractals',
    name: 'ZOI Fractals',
    description: 'Fractal breakout signals',
    icon: Grid3x3,
    color: 'text-pink-500',
    defaultConfig: { minConfidence: 80, stopLoss: 2, takeProfit: 5 }
  }
]

// Quantitative Strategy Templates
const QUANT_STRATEGIES = [
  {
    id: 'momentum',
    chartType: 'candlestick',
    name: 'Momentum',
    description: 'Follows strong trends and breakouts',
    icon: Rocket,
    color: 'text-blue-500',
    defaultConfig: { minConfidence: 75, stopLoss: 2, takeProfit: 6 }
  },
  {
    id: 'mean-reversion',
    chartType: 'candlestick',
    name: 'Mean Reversion',
    description: 'Trades oversold/overbought conditions',
    icon: Waves,
    color: 'text-purple-500',
    defaultConfig: { minConfidence: 65, stopLoss: 1.5, takeProfit: 3 }
  },
  {
    id: 'scalping',
    chartType: 'candlestick',
    name: 'Scalping',
    description: 'Quick trades for small profits',
    icon: Zap,
    color: 'text-yellow-500',
    defaultConfig: { minConfidence: 80, stopLoss: 0.5, takeProfit: 1 }
  }
]

// Combine all strategies
const ALL_STRATEGIES = [...CHART_STRATEGIES, ...QUANT_STRATEGIES]


export function BacktestingPanel({ symbol }: BacktestingPanelProps) {
  const { chartType, candleData, dataReady } = useChartData()
  const [isRunning, setIsRunning] = useState(false)
  const [result, setResult] = useState<BacktestResult | null>(null)
  const [selectedStrategy, setSelectedStrategy] = useState('otc') // Default to OTC

  // Debug: Log data status
  console.log('📊 Backtest Panel - Data Status:', {
    dataReady,
    candleCount: candleData?.length || 0,
    chartType,
    symbol
  })

  // Backtest configuration
  const [initialBalance, setInitialBalance] = useState(10000)
  const [amount, setAmount] = useState(0.001)
  const [maxPositions, setMaxPositions] = useState(3)
  const [minConfidence, setMinConfidence] = useState(70)
  const [stopLossEnabled, setStopLossEnabled] = useState(true)
  const [stopLossPercent, setStopLossPercent] = useState(2)
  const [takeProfitEnabled, setTakeProfitEnabled] = useState(true)
  const [takeProfitPercent, setTakeProfitPercent] = useState(5)

  const currentStrategy = ALL_STRATEGIES.find(s => s.id === selectedStrategy)

  const handleStrategyChange = (strategyId: string) => {
    setSelectedStrategy(strategyId)
    const strategy = ALL_STRATEGIES.find(s => s.id === strategyId)
    if (strategy) {
      setMinConfidence(strategy.defaultConfig.minConfidence)
      setStopLossPercent(strategy.defaultConfig.stopLoss)
      setTakeProfitPercent(strategy.defaultConfig.takeProfit)
    }
  }

  const runBacktest = async () => {
    // Use chart data if available, otherwise generate mock data
    let testData = candleData;
    
    if (!testData || testData.length === 0) {
      console.log('📊 Chart context data not available, generating mock data for backtest...');
      // Generate mock data for backtesting
      const mockData = [];
      const basePrice = 45000;
      const startTime = Date.now() - (100 * 60 * 1000); // 100 minutes of data
      
      for (let i = 0; i < 100; i++) {
        const time = startTime + (i * 60 * 1000);
        const priceChange = (Math.random() - 0.5) * 200;
        const currentPrice = basePrice + (i * 10) + priceChange;
        
        const open = currentPrice + (Math.random() - 0.5) * 50;
        const close = open + (Math.random() - 0.5) * 100;
        const high = Math.max(open, close) + Math.random() * 50;
        const low = Math.min(open, close) - Math.random() * 50;
        const volume = Math.floor(Math.random() * 1000000) + 500000;
        
        mockData.push({
          time,
          open,
          high,
          low,
          close,
          volume
        });
      }
      
      testData = mockData;
      console.log('📊 Generated mock data for backtest:', testData.length, 'candles');
    }

    // testData is already set above (either from context or mock generated)

    setIsRunning(true)

    try {
      const strategy = ALL_STRATEGIES.find(s => s.id === selectedStrategy)
      const config: AutoTradingConfig = {
        enabled: true,
        chartType: strategy?.chartType || chartType, // Use strategy's chart type
        timeframe: '1m',
        amount: amount,
        leverage: 1,
        stopLoss: {
          enabled: stopLossEnabled,
          percentage: stopLossPercent
        },
        takeProfit: {
          enabled: takeProfitEnabled,
          percentage: takeProfitPercent
        },
        maxPositions: maxPositions,
        riskPerTrade: 1,
        minConfidence: minConfidence
      }

      console.log('🚀 Starting backtest with:', {
        strategy: selectedStrategy,
        chartType: strategy?.chartType || chartType,
        dataPoints: testData.length,
        contextDataReady: dataReady,
        config
      })

      const backtestService = new BacktestingService(config, initialBalance)
      const backtestResult = await backtestService.runBacktest(testData, config)

      console.log('✅ Backtest completed:', backtestResult)

      if (!backtestResult || backtestResult.totalTrades === 0) {
        console.warn('⚠️ Backtest completed with no trades - check signal generation')
        alert('Backtest completed but no trades were executed. This might be due to high confidence thresholds or insufficient signal opportunities.')
      }

      setResult(backtestResult)
    } catch (error) {
      console.error('❌ Backtest error:', error)
      alert(`Backtest failed: ${error instanceof Error ? error.message : 'Unknown error'}. Check console for details.`)
    } finally {
      setIsRunning(false)
    }
  }

  return (
    <div className="h-full flex flex-col">
      <ScrollArea className="flex-1">
        <div className="p-3 space-y-3">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <BarChart3 className="h-4 w-4 text-primary" />
              <h3 className="text-sm font-semibold">Strategy Backtesting</h3>
            </div>
            {result && (
              <Badge
                variant={result.totalPnLPercentage >= 0 ? "default" : "destructive"}
                className="text-xs"
              >
                {result.totalPnLPercentage >= 0 ? '✓ Profitable' : '✗ Loss'}
              </Badge>
            )}
          </div>

          {/* Data Status - Compact */}
          {(!dataReady || !candleData || candleData.length === 0) && (
            <Card className="border-yellow-500/30 bg-yellow-500/5">
              <CardContent className="p-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <AlertCircle className="h-3.5 w-3.5 text-yellow-500" />
                    <span className="text-xs text-yellow-600 dark:text-yellow-400">
                      Waiting for chart data - please view chart panel first
                    </span>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {candleData?.length || 0} candles loaded
                  </div>
                </div>
                {candleData && candleData.length > 0 && (
                  <div className="mt-1 text-xs text-green-600 dark:text-green-400">
                    ✅ Chart data ready ({candleData.length} candles available)
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          <Tabs defaultValue="config" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="config">Configuration</TabsTrigger>
              <TabsTrigger value="results">Results</TabsTrigger>
            </TabsList>

            {/* Configuration Tab */}
            <TabsContent value="config" className="space-y-3 m-0 mt-2">
              {/* Strategy Selection */}
              <Card>
                <CardHeader className="p-3 pb-2">
                  <Label className="text-xs font-semibold">Strategy</Label>
                </CardHeader>
                <CardContent className="p-3 pt-0 space-y-2">
                  <Select value={selectedStrategy} onValueChange={handleStrategyChange}>
                    <SelectTrigger className="h-9 text-xs">
                      <SelectValue>
                        {currentStrategy && (
                          <div className="flex items-center space-x-2">
                            <currentStrategy.icon className={cn("h-3.5 w-3.5", currentStrategy.color)} />
                            <span>{currentStrategy.name}</span>
                          </div>
                        )}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">Chart Strategies</div>
                      {CHART_STRATEGIES.map((strategy) => (
                        <SelectItem key={strategy.id} value={strategy.id}>
                          <div className="flex items-center space-x-2">
                            <strategy.icon className={cn("h-3.5 w-3.5", strategy.color)} />
                            <div>
                              <div className="font-medium">{strategy.name}</div>
                              <div className="text-xs text-muted-foreground">{strategy.description}</div>
                            </div>
                          </div>
                        </SelectItem>
                      ))}
                      <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground border-t mt-1 pt-2">Quant Strategies</div>
                      {QUANT_STRATEGIES.map((strategy) => (
                        <SelectItem key={strategy.id} value={strategy.id}>
                          <div className="flex items-center space-x-2">
                            <strategy.icon className={cn("h-3.5 w-3.5", strategy.color)} />
                            <div>
                              <div className="font-medium">{strategy.name}</div>
                              <div className="text-xs text-muted-foreground">{strategy.description}</div>
                            </div>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </CardContent>
              </Card>

              {/* Basic Settings */}
              <Card>
                <CardHeader className="p-3 pb-2">
                  <Label className="text-xs font-semibold">Basic Settings</Label>
                </CardHeader>
                <CardContent className="p-3 pt-0 space-y-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Initial Balance ($)</Label>
                    <Input
                      type="number"
                      value={initialBalance}
                      onChange={(e) => setInitialBalance(parseFloat(e.target.value) || 10000)}
                      className="h-8 text-xs"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1.5">
                      <Label className="text-xs">Amount per Trade</Label>
                      <Input
                        type="number"
                        value={amount}
                        onChange={(e) => setAmount(parseFloat(e.target.value) || 0.001)}
                        className="h-8 text-xs"
                        step="0.001"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Max Positions</Label>
                      <Input
                        type="number"
                        value={maxPositions}
                        onChange={(e) => setMaxPositions(parseInt(e.target.value) || 1)}
                        className="h-8 text-xs"
                        min="1"
                        max="10"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Risk Management */}
              <Card>
                <CardHeader className="p-3 pb-2">
                  <Label className="text-xs font-semibold">Risk Management</Label>
                </CardHeader>
                <CardContent className="p-3 pt-0 space-y-3">
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs">Stop Loss ({stopLossPercent}%)</Label>
                      <input
                        type="checkbox"
                        checked={stopLossEnabled}
                        onChange={(e) => setStopLossEnabled(e.target.checked)}
                        className="h-3 w-3"
                      />
                    </div>
                    {stopLossEnabled && (
                      <Slider
                        value={[stopLossPercent]}
                        onValueChange={(value) => setStopLossPercent(value[0])}
                        min={0.5}
                        max={10}
                        step={0.5}
                        className="py-2"
                      />
                    )}
                  </div>

                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs">Take Profit ({takeProfitPercent}%)</Label>
                      <input
                        type="checkbox"
                        checked={takeProfitEnabled}
                        onChange={(e) => setTakeProfitEnabled(e.target.checked)}
                        className="h-3 w-3"
                      />
                    </div>
                    {takeProfitEnabled && (
                      <Slider
                        value={[takeProfitPercent]}
                        onValueChange={(value) => setTakeProfitPercent(value[0])}
                        min={1}
                        max={20}
                        step={0.5}
                        className="py-2"
                      />
                    )}
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-xs">Min Confidence ({minConfidence}%)</Label>
                    <Slider
                      value={[minConfidence]}
                      onValueChange={(value) => setMinConfidence(value[0])}
                      min={50}
                      max={95}
                      step={5}
                      className="py-2"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Run Button */}
              <Button
                onClick={runBacktest}
                disabled={false}
                className="w-full"
              >
                {isRunning ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Running Backtest...
                  </>
                ) : (
                  <>
                    <Play className="h-4 w-4 mr-2" />
                    Run Backtest
                  </>
                )}
              </Button>
            </TabsContent>

            {/* Results Tab */}
            <TabsContent value="results" className="space-y-3 m-0 mt-2">
              {!result ? (
                <Card>
                  <CardContent className="p-8 text-center text-muted-foreground">
                    <Activity className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No backtest results yet</p>
                    <p className="text-xs mt-1">Configure and run a backtest to see results</p>
                  </CardContent>
                </Card>
              ) : (
                <>
                  {/* Summary Metrics */}
                  <Card>
                    <CardHeader className="p-3 pb-2">
                      <CardTitle className="text-sm">Performance Summary</CardTitle>
                    </CardHeader>
                    <CardContent className="p-3 pt-0 space-y-2">
                      <div className="grid grid-cols-2 gap-2">
                        <div className="bg-muted/50 rounded-md p-2">
                          <div className="text-[10px] text-muted-foreground">Total P&L</div>
                          <div className={cn(
                            "text-sm font-bold",
                            result.totalPnL >= 0 ? "text-green-500" : "text-red-500"
                          )}>
                            {result.totalPnL >= 0 ? '+' : ''}${result.totalPnL.toFixed(2)}
                          </div>
                          <div className="text-[10px] text-muted-foreground">
                            {result.totalPnLPercentage >= 0 ? '+' : ''}{result.totalPnLPercentage.toFixed(2)}%
                          </div>
                        </div>

                        <div className="bg-muted/50 rounded-md p-2">
                          <div className="text-[10px] text-muted-foreground">Win Rate</div>
                          <div className="text-sm font-bold">{result.winRate.toFixed(1)}%</div>
                          <div className="text-[10px] text-muted-foreground">
                            {result.winningTrades}W / {result.losingTrades}L
                          </div>
                        </div>

                        <div className="bg-muted/50 rounded-md p-2">
                          <div className="text-[10px] text-muted-foreground">Total Trades</div>
                          <div className="text-sm font-bold">{result.totalTrades}</div>
                          <div className="text-[10px] text-muted-foreground">
                            {Math.round(result.totalTrades / (result.duration / (1000 * 60 * 60 * 24)))} per day
                          </div>
                        </div>

                        <div className="bg-muted/50 rounded-md p-2">
                          <div className="text-[10px] text-muted-foreground">Max Drawdown</div>
                          <div className="text-sm font-bold text-red-500">
                            -{result.maxDrawdown.toFixed(2)}%
                          </div>
                          <div className="text-[10px] text-muted-foreground">Peak to valley</div>
                        </div>

                        <div className="bg-muted/50 rounded-md p-2">
                          <div className="text-[10px] text-muted-foreground">Profit Factor</div>
                          <div className="text-sm font-bold">
                            {result.profitFactor === Infinity ? '∞' : result.profitFactor.toFixed(2)}
                          </div>
                          <div className="text-[10px] text-muted-foreground">Gross profit/loss</div>
                        </div>

                        <div className="bg-muted/50 rounded-md p-2">
                          <div className="text-[10px] text-muted-foreground">Sharpe Ratio</div>
                          <div className="text-sm font-bold">{result.sharpeRatio.toFixed(2)}</div>
                          <div className="text-[10px] text-muted-foreground">Risk-adjusted</div>
                        </div>
                      </div>

                      <Separator />

                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div>
                          <span className="text-muted-foreground">Start Balance:</span>
                          <span className="ml-1 font-medium">${result.startBalance.toFixed(2)}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">End Balance:</span>
                          <span className="ml-1 font-medium">${result.endBalance.toFixed(2)}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Avg Win:</span>
                          <span className="ml-1 font-medium text-green-500">
                            +${result.averageWin.toFixed(2)}
                          </span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Avg Loss:</span>
                          <span className="ml-1 font-medium text-red-500">
                            ${result.averageLoss.toFixed(2)}
                          </span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Trade List */}
                  <Card>
                    <CardHeader className="p-3 pb-2">
                      <CardTitle className="text-sm">Recent Trades ({result.trades.length})</CardTitle>
                    </CardHeader>
                    <CardContent className="p-3 pt-0 space-y-1.5 max-h-[300px] overflow-y-auto">
                      {result.trades.slice(-10).reverse().map((trade, idx) => (
                        <div key={trade.id} className="flex items-center justify-between p-2 bg-muted/50 rounded-md text-xs">
                          <div className="flex items-center space-x-2">
                            <Badge className={cn(
                              "text-[10px] px-1.5",
                              trade.type === 'BUY' ? 'bg-green-500' : 'bg-red-500'
                            )}>
                              {trade.type}
                            </Badge>
                            <div>
                              <div>${trade.entryPrice.toFixed(2)} → ${trade.exitPrice?.toFixed(2) || '?'}</div>
                              <div className="text-[10px] text-muted-foreground">{trade.exitReason}</div>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className={cn(
                              "font-bold",
                              (trade.pnl || 0) >= 0 ? "text-green-500" : "text-red-500"
                            )}>
                              {(trade.pnl || 0) >= 0 ? '+' : ''}${(trade.pnl || 0).toFixed(2)}
                            </div>
                            <div className="text-[10px] text-muted-foreground">
                              {(trade.pnlPercentage || 0).toFixed(2)}%
                            </div>
                          </div>
                        </div>
                      ))}
                    </CardContent>
                  </Card>

                  {/* Strategy Used */}
                  <Card className="border-blue-500/30 bg-blue-500/5">
                    <CardContent className="p-3 text-xs">
                      <div className="font-medium text-blue-600 dark:text-blue-400 mb-1">Strategy Used</div>
                      <div className="text-muted-foreground">
                        {result.config.chartType.toUpperCase()} • Min Confidence: {result.config.minConfidence}%
                        {result.config.stopLoss.enabled && ` • SL: ${result.config.stopLoss.percentage}%`}
                        {result.config.takeProfit.enabled && ` • TP: ${result.config.takeProfit.percentage}%`}
                      </div>
                    </CardContent>
                  </Card>
                </>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </ScrollArea>
    </div>
  )
}

